import { createTextOrder, createMediaOrder, updateOrderApi } from "../api/orderApi";
import store from '@/store/store';

export { createOrder, getStatusLabel, getStatusSlug, updateOrder, buildSavePayload };

const createOrder = async (bodyParams, type = "text") => {
  const user = store.getters.getUser;
  bodyParams.user_id = user.id;
  bodyParams.workspace_id = store.getters.selectedWorkspace?.id ?? '';

  let response;

  switch (type) {
    case "media":
      response = await createMediaOrder(bodyParams);
      break;
    case "text":
    default:
      response = await createTextOrder(bodyParams);
  }

  console.log('createOrder response', response);

  return response;
}

/**
 * Tool il cui contenuto editabile non vive in `order.content` ma nel componente padre,
 * che lo passa a ContentToolbar tramite la prop `parentContent`.
 */
const PARENT_CONTENT_SLUGS = [
  "blog_post_paragraphs",
  "blog_article_with_social_posts",
  "blog_legal_article_with_social_posts",
];

/**
 * Costruisce i dati da inviare a `services/orders/update` per un order detail.
 *
 * Usato sia dal salvataggio manuale (ContentToolbar) sia dall'autosave, così i due
 * percorsi inviano per costruzione esattamente lo stesso contenuto.
 *
 * @param {object} order - l'order dello store
 * @param {string} parentContent - contenuto gestito dal componente padre (vedi PARENT_CONTENT_SLUGS)
 * @returns {{orderDetailId: number, content: string, title: string|null}|null} null se non c'è nulla di salvabile
 */
const buildSavePayload = (order, parentContent = "") => {
  if (!order || !order.order_detail_id) {
    return null;
  }

  let content = order.content;

  if (PARENT_CONTENT_SLUGS.includes(order.slug)) {
    content = parentContent;
  } else if (typeof content !== "string" && content?.blog_post_html) {
    content = content.blog_post_html;
  }

  // Mai sovrascrivere un contenuto esistente con un valore vuoto o non testuale
  if (typeof content !== "string" || content.trim() === "") {
    if (typeof order.content === "string" && order.content.trim() !== "") {
      content = order.content;
    } else {
      return null;
    }
  }

  return {
    orderDetailId: order.order_detail_id,
    content,
    title: order.title ?? null,
  };
};

/**
 * Salva contenuto e titolo di un order detail.
 *
 * @param {number} id - order_detail_id
 * @param {string} content
 * @param {string|null} title
 * @param {object} options
 * @param {boolean} options.refetch - se true ricarica l'order nello store (rimonta l'editor:
 *                                    da evitare durante l'autosave, perché perde il cursore)
 * @param {boolean} options.autosave - se true il backend non crea una versione in history
 */
const updateOrder = async (id, content, title = null, options = {}) => {
  const { refetch = true, autosave = false } = options;
  const user = store.getters.getUser;

  if (user) {
    const bodyParams = {
      user_id: user.id,
      order_detail_id: id,
      content: content,
      title: title
    };

    if (autosave) {
      bodyParams.autosave = true;
    }

    let response;
    response = await updateOrderApi(bodyParams);

    if (refetch) {
      await store.dispatch('fetchOrder', {id: id});
    }

    return response;
  }

  throw Error;
};

/**
 * Maps backend status to frontend status label.
 *
 * @param {string} status - The status from the backend to be mapped.
 * @returns {string} - The corresponding frontend status label.
 */
const getStatusLabel = (status) => {
  switch (status) {
    case "failed":
      return "Failed";
    case "accepted":
      return "Accepted";
    case "delivered":
      return "Delivered";
    case "rejected":
      return "Rejected";
    case "refunded":
      return "Refunded"
    default:
      return "In Progress";
  }
};

/**
 * Maps frontend status label to backand.
 *
 * @param {string} status - The status from the frontend label to be mapped.
 * @returns {array} - The corresponding backend status.
 */
const getStatusSlug = (status) => {
  switch (status) {
    case "failed":
      return ["failed"];
    case "accepted":
      return ["accepted", "delivered"];
    case "in_progress":
      return ["pending", "waiting_queue", "processing"];
    case "all":
    default:
      return [];
  }
};
